import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { FiSearch, FiEye, FiHeart, FiMessageSquare, FiBookmark } from 'react-icons/fi';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { PageContainer } from '../App';

const client = generateClient();

// Add getS3ImageUrl helper function
const getS3ImageUrl = async (key) => {
  if (!key) return null;
  try {
    const { url } = await getUrl({ path: key });
    return url;
  } catch (err) {
    console.error('Error getting image URL:', err);
    return null;
  }
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchQuery = searchParams.get('q') || '';
  const [bookmarkedIds, setBookmarkedIds] = useState([]);

  useEffect(() => {
    const searchArticles = async () => {
      if (!searchQuery.trim()) {
        setArticles([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const searchInput = searchQuery.trim();
        const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        const capitalizeWords = (str) => str.split(' ').map(word => capitalizeFirstLetter(word)).join(' ');
        
        const searchFilter = {
          or: [
            // Original input
            { title: { contains: searchInput } },
            { content: { contains: searchInput } },
            // Lowercase
            { title: { contains: searchInput.toLowerCase() } },
            { content: { contains: searchInput.toLowerCase() } },
            // Uppercase
            { title: { contains: searchInput.toUpperCase() } },
            { content: { contains: searchInput.toUpperCase() } },
            // Capitalize first letter
            { title: { contains: capitalizeFirstLetter(searchInput) } },
            { content: { contains: capitalizeFirstLetter(searchInput) } },
            // Capitalize each word
            { title: { contains: capitalizeWords(searchInput) } },
            { content: { contains: capitalizeWords(searchInput) } }
          ]
        };

        const {data} = await client.models.Article.list({
          filter: searchFilter
        });
        
        // Remove duplicates and process images
        const uniqueArticleIds = Array.from(new Set(data.map(a => a.id)));
        const processedArticles = await Promise.all(
          uniqueArticleIds.map(async id => {
            const article = data.find(a => a.id === id);
            const fullImageUrl = await getS3ImageUrl(article.bannerImageUrl);
            return {
              ...article,
              bannerImageUrl: fullImageUrl
            };
          })
        );
        
        setArticles(processedArticles);
      } catch (err) {
        console.error('Error searching articles:', err);
        setError('Failed to search articles. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    searchArticles();
  }, [searchQuery]);

  useEffect(() => {
    // Load bookmarked IDs from localStorage
    const ids = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
    setBookmarkedIds(ids);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const query = formData.get('search');
    setSearchParams({ q: query });
  };

  return (
    <PageContainer>
      <SearchContent>
        <SearchBar>
          <SearchForm onSubmit={handleSearch}>
            <SearchInputWrapper>
              <SearchIcon />
              <SearchInput
                name="search"
                placeholder="Search articles"
                defaultValue={searchQuery}
              />
            </SearchInputWrapper>
          </SearchForm>
        </SearchBar>

        {loading ? (
          <LoadingMessage>Searching articles...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : articles.length > 0 ? (
          <>
            <ResultsCount>
              {articles.length} {articles.length === 1 ? 'result' : 'results'} for "{searchQuery}"
            </ResultsCount>
            <ArticleFeed>
              {articles.map((article) => (
                <ArticleCard key={article.id} as={Link} to={`/article/${article.id}`}>
                  {article.bannerImageUrl && (
                    <ArticleImage>
                      <img src={article.bannerImageUrl} alt={article.title} />
                    </ArticleImage>
                  )}
                  <ArticleCardContent>
                    <Author>
                      <AuthorImage />
                      <AuthorName>{article.author}</AuthorName>
                    </Author>
                    <ArticleTitle>
                      {article.title}
                    </ArticleTitle>
                    <ArticlePreview>
                      {article.content?.slice(0, 300) || article.description?.slice(0, 300)}
                      {((article.content && article.content.length > 300) || (article.description && article.description.length > 300)) && '...'}
                    </ArticlePreview>
                    <ArticleMeta>
                      <span>
                        {new Date(article.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })} · {article.readTime || 5} min read
                      </span>
                      <MetricItem>
                        <FiEye /> {article.views || 0}
                      </MetricItem>
                      <MetricItem>
                        <FiHeart /> {article.likes || 0}
                      </MetricItem>
                      <MetricItem>
                        <FiMessageSquare /> {article.commentsCount || 0}
                      </MetricItem>
                      <MetricItem isBookmarked={bookmarkedIds.includes(article.id)}>
                        <FiBookmark />
                      </MetricItem>
                    </ArticleMeta>
                  </ArticleCardContent>
                </ArticleCard>
              ))}
            </ArticleFeed>
          </>
        ) : searchQuery ? (
          <NoResults>No articles found for "{searchQuery}"</NoResults>
        ) : null}
      </SearchContent>
    </PageContainer>
  );
};

const SearchContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SearchBar = styled.div`
  margin-bottom: 2rem;
  padding-top: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-top: 0.5rem;
  }
`;

const SearchForm = styled.form`
  width: 100%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #757575;
  width: 20px;
  height: 20px;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    left: 0.8rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  font-size: 1.2rem;
  border: 1px solid #e6e6e6;
  border-radius: 99px;
  outline: none;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 0.8rem 0.8rem 2.5rem;
  }

  &:focus {
    border-color: #1a8917;
  }
`;

const ResultsCount = styled.div`
  color: #757575;
  margin-bottom: 2rem;
  font-size: 1.1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ArticleFeed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ArticleCard = styled.article`
  background: white;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 4px;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ArticleImage = styled.div`
  width: 280px;
  flex-shrink: 0;
  background: #f8f9fa;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
    position: relative;
  }
`;

const ArticleCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const AuthorImage = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: url('/author.jpeg');
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AuthorName = styled.span`
  font-weight: 500;
  color: #292929;
`;

const ArticleTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  font-family: gt-super, Georgia, serif;
  color: #292929;
`;

const ArticlePreview = styled.p`
  color: #757575;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const ArticleMeta = styled.div`
  color: #757575;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: ${props => props.isBookmarked ? '#1a8917' : '#757575'};

  svg {
    fill: ${props => props.isBookmarked ? 'currentColor' : 'none'};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #e53935;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

export default SearchPage; 